import React, { useState, useCallback } from 'react';
import RichTextEditor from '../RichTextEditor';
import { Code, FileText } from 'lucide-react';

/**
 * Dual-mode soul file editor — toggle between rich text (TipTap) and raw markdown.
 * Props:
 *   value: string (markdown content)
 *   onChange: (markdown: string) => void
 *   placeholder: string
 */
export default function SoulEditor({ value = '', onChange, placeholder = 'Define your agent\'s personality, goals, and constraints...' }) {
    const [mode, setMode] = useState('rich'); // 'rich' | 'markdown'

    const handleRichChange = useCallback((html) => {
        // Store the HTML — we'll convert to MD on save if needed
        onChange(html);
    }, [onChange]);

    return (
        <div className="space-y-2">
            {/* Mode Toggle */}
            <div className="flex items-center gap-1 border border-stone-200 rounded-lg p-0.5 w-fit bg-stone-50">
                <button
                    type="button"
                    onClick={() => setMode('rich')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        mode === 'rich' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                    }`}
                >
                    <FileText className="w-3.5 h-3.5" /> Rich Text
                </button>
                <button
                    type="button"
                    onClick={() => setMode('markdown')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        mode === 'markdown' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                    }`}
                >
                    <Code className="w-3.5 h-3.5" /> Markdown
                </button>
            </div>

            {/* Editor */}
            {mode === 'rich' ? (
                <RichTextEditor
                    content={value}
                    onChange={handleRichChange}
                    placeholder={placeholder}
                    minHeight="300px"
                />
            ) : (
                <div className="relative">
                    <textarea
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        placeholder={placeholder}
                        className="w-full min-h-[300px] px-4 py-3 font-mono text-sm text-stone-800 bg-stone-50 border border-stone-200 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-stone-300 leading-relaxed"
                        spellCheck={false}
                    />
                    <div className="absolute top-2 right-2 text-[10px] text-stone-400 bg-stone-100 px-2 py-0.5 rounded">
                        MD
                    </div>
                </div>
            )}

            {/* Merge field hints */}
            <p className="text-[10px] text-stone-400">
                Available merge fields: <code className="bg-stone-100 px-1 rounded">{'{{org_name}}'}</code> <code className="bg-stone-100 px-1 rounded">{'{{agent_name}}'}</code>
            </p>
        </div>
    );
}
